/**
 * Super Admin · Master Rule Library - FOCTTA-curated reference rules.
 *
 * Each curated rule shows its Rego excerpt, regulatory grounding and
 * industry anchors. Corrected 10 Oct 2026 (R3-OI-192): the excerpts do not
 * run anywhere, and promotion to a tenant is not implemented (the server
 * writes nothing), so the page no longer offers "Promote to <tenant>" or
 * shows invented distribution counts.
 */
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Library, GitBranch } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { listMasterRules, type CuratedRule } from '@/lib/api';

export function SuperAdminMasterRuleLibrary(): JSX.Element {
  const [rules, setRules] = useState<CuratedRule[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listMasterRules()
      .then((r) => setRules(r.rules))
      .catch((e) => setError(e instanceof Error ? e.message : 'unknown'));
  }, []);

  return (
    <PageContainer>
      <PageHeader
        title="Super Admin · Master Rule Library"
        subtitle="FOCTTA-curated reference rules, each mapped to an Indian or international regulation. The Rego excerpts are reference text: they do not run in the Decision Gateway, and promoting a rule to a tenant is not implemented."
        badges={[
          { label: 'Platform-curated', variant: 'glass' },
          { label: rules ? `${rules.length} rules` : '-', variant: 'info' },
        ]}
      />

      {error && (
        <GlassCard className="p-4 mb-4 border-destructive">
          <p className="text-xs text-destructive">{error} - this page requires platform_super_admin / platform_ops actor role.</p>
        </GlassCard>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {rules?.map((r, i) => (
          <motion.div key={r.ruleId} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
            <GlassCard className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Library className="h-4 w-4 text-primary" />
                  <Badge variant={r.severity === 'critical' ? 'critical' : r.severity === 'high' ? 'warning' : 'glass'} className="text-[9px] uppercase">{r.severity}</Badge>
                  <Badge variant="info" className="text-[9px]">{r.category}</Badge>
                </div>
                <Badge variant="glass" className="text-[9px]">not distributed</Badge>
              </div>

              <CardTitle className="text-sm leading-tight">{r.name}</CardTitle>
              <code className="text-[10px] font-mono opacity-70">{r.ruleId}</code>

              <p className="text-xs text-muted-foreground mt-3 leading-relaxed">{r.rationale}</p>

              <div className="mt-3 flex flex-wrap gap-1">
                {r.industryAnchors.map((a) => (
                  <Badge key={a} variant="glass" className="text-[9px]">{a === '*' ? 'all industries' : a.replace(/_/g, ' ')}</Badge>
                ))}
              </div>

              <details className="mt-3 group">
                <summary className="text-[10px] uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground">
                  Rego body
                </summary>
                <pre className="text-[10px] font-mono opacity-80 mt-2 p-2 glass-lite rounded-md overflow-x-auto whitespace-pre-wrap">{r.regoBodyExcerpt}</pre>
              </details>

              <Button
                size="sm"
                variant="glass"
                className="w-full mt-3"
                disabled
                title="Promotion to a tenant is not implemented: nothing would be written."
              >
                Promotion not implemented
              </Button>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </PageContainer>
  );
}
